import { html } from '~/lib/bn-lit-element';
import './import-list';
import './import-show';
import './parts/bin-import-summary';
import { ImportIndex } from './import-index';
import { renderIf } from '../utility/lit-entity';
import { renderRecordPreviewValue } from './helpers/import-mapping-helpers';

class BinImportIndex extends ImportIndex {
  _renderList({ href }) {
    return html`
      <import-list
        .href="${href}"
        title="Bin Imports"
        description="Use this page to import new bins and organizations from a CSV file"
        import-show-path="/bin-imports/show"
      ></import-list>
    `;
  }

  _renderImport({ href, path }) {
    return html`
      <import-show
        .href="${href}"
        .path="${path}"
        title="Bin Import"
        new-item-count-warning-text="New bin count does not match CSV file record count. Please verify there are no duplicate entries in the file."
        .importButtonDescriptionRenderer="${this._renderImportButtonDescription}"
        .targetGroupGenerator="${this._generateTargetGroups}"
        .importPreviewRenderer="${this._renderImportPreview}"
        .extraItemRenderer="${this._renderExtraItems.bind(this)}"
      ></import-show>
    `;
  }

  _renderImportButtonDescription(importEntity) {
    const { newItemCount, newOrganizationCount } = importEntity.properties;
    return html`<span class="item-count">${newItemCount || 0}</span> new bins
    and <span class="item-count">${newOrganizationCount || 0}</span> new organizations`;
  }

  _generateTargetGroups(importMappingEntity) {
    const mappingTargets = importMappingEntity.getSubEntities('https://api.binsentry.com/rel/mapping-target');
    const customerTargets = [
      mappingTargets.find((target) => target.properties.target === 'organizations[department]'),
      mappingTargets.find((target) => target.properties.target === 'organizations[customer]'),
    ].filter(target => target);
    const farmTargets = [
      mappingTargets.find((target) => target.properties.target === 'organizations[farm]'),
      mappingTargets.find((target) => target.properties.target === 'bin[address]'),
      mappingTargets.find((target) => target.properties.target === 'bin[latitude]'),
      mappingTargets.find((target) => target.properties.target === 'bin[longitude]'),
    ].filter(target => target);
    const barnTargets = mappingTargets.filter((target) => target.properties.target === 'organizations[barn]');
    const binTargets = mappingTargets.filter((target) => {
      const { target: targetId } = target.properties;
      return targetId.startsWith('bin') && !['bin[address]', 'bin[latitude]', 'bin[longitude]'].includes(targetId);
    });
    const binTagTargets = mappingTargets.filter((target) => target.properties.target.startsWith('tags'));

    return [
      { name: 'Customer', targets: customerTargets },
      { name: 'Location', targets: farmTargets },
      { name: 'Barn', targets: barnTargets },
      { name: 'Bin', targets: binTargets },
      { name: 'Bin Tags', targets: binTagTargets },
    ];
  }

  _renderImportPreview({ mapping, recordPreview }) {
    const { bin, organizations, tags } = mapping;
    return html`
      ${renderIf(organizations.department || organizations.customer, () => html`
        <div class="properties">
          ${renderRecordPreviewValue('Department', organizations.department, recordPreview)}
          ${renderRecordPreviewValue('Customer', organizations.customer, recordPreview)}
        </div>
      `)}
      <div class="properties">
        ${renderRecordPreviewValue('Farm / Location', organizations.farm, recordPreview)}
        ${renderRecordPreviewValue('Address', bin.address, recordPreview)}
        ${renderRecordPreviewValue('Latitude', bin.latitude, recordPreview)}
        ${renderRecordPreviewValue('Longitude', bin.longitude, recordPreview)}
      </div>
      <div class="properties">
        ${renderRecordPreviewValue('Barn', organizations.barn, recordPreview)}
      </div>
      <div class="properties">
        ${renderRecordPreviewValue('Bin', bin.name, recordPreview)}
        ${renderRecordPreviewValue('Capacity', bin.capacity, recordPreview)}
        ${renderRecordPreviewValue('Model', bin.model, recordPreview)}
        ${renderRecordPreviewValue('Bulk Density', bin.bulkDensity, recordPreview)}
      </div>
      ${renderIf(tags, () => html`
        <div class="properties">
          ${Object.keys(tags).map((key) => html`
            ${renderRecordPreviewValue(key, tags[key], recordPreview)}
          `)}
        </div>
      `)}
    `;
  }

  _renderExtraItems(importEntity) {
    const { state } = importEntity.properties;
    if (state !== 'completed') {
      return html``;
    }

    return html`
      <bin-import-summary href="${importEntity.getLink('self').href}"></bin-import-summary>
    `;
  }
}

window.customElements.define('bin-import-index', BinImportIndex);
